import { Injectable } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { ConditionSet, Control, DynamicForm, Rule, Validator } from "../models/dynamic-form";
import { autocompleteValidator } from "../validators/autoCompleteValidator";
import { selectValidator } from "../validators/select-validator";
import { PermissionConditionSet, PermissionService } from "./permission.service";
import { DateTimeValidator } from "../validators/dateTimeValidator";
import { CustomValidators } from "../validators/custom-validators";

@Injectable({
  providedIn: "root"
})
export class DynamicFormService {

  validatorMap: { [key: string]: (params?: any) => ValidatorFn } = {
    "required": () => Validators.required,
    "minlength": (params: number) => Validators.minLength(params),
    "maxlength": (params: number) => Validators.maxLength(params),
    "email": () => Validators.email,
    "pattern": (params: string) => CustomValidators.pattern(params, "Invalid pattern"),
    "min": (params: number) => Validators.min(params),
    "max": (params: number) => Validators.max(params),
    "integerOnly": () => (control: any) => {
      const value = control.value;
      return value != null && value % 1 !== 0 ? { integerOnly: true } : null;
    },
    "percentage": ()=>Validators.pattern(/^(100(\.0{1,2})?|[1-9]?\d(\.\d{1,2})?)$/),



    //TODO: Not required, we can remove this post discussion
    "autocompleteRequired": () => autocompleteValidator(),
    "selectRequired": () => selectValidator(),
    "dateTimeRequired": () => DateTimeValidator()
  };

  constructor(
    private fb: FormBuilder,
    private permissionService: PermissionService
  ) {
  }

  addControlsToForm(formGroup: FormGroup, config: DynamicForm, overrideExisting: boolean = false): FormGroup {
    config.formRows
      .flatMap(row => row.formGroups)
      .forEach(group => {
        const control = group.control;

        const isHidden = control.hidden ?? false;
        const isVisible = !isHidden && this.evaluateConditionSet(control?.conditions?.visibility, formGroup, true) && this.evaluatePermissionConditionSet(control.permissionConditions?.visibility, true);
        const isDisabled = !isVisible || (control?.disabled ?? false) || this.evaluateConditionSet(control?.conditions?.disabled, formGroup, false) || this.evaluatePermissionConditionSet(control.permissionConditions?.disabled, false);
        const validators = this.getValidators(control.validators || [], formGroup);

        // control.hidden = !isVisible;
        if (formGroup.contains(control.label)) {
          if (overrideExisting) {
            formGroup.removeControl(control.label);
          }
        }
        formGroup.addControl(
          control.label,
          this.fb.control({
            value: control?.defaultValue !== undefined ? control.defaultValue : null,
            disabled: isDisabled

          }, validators)
        );
      });
    return formGroup;
  }

  isControlRequired(control: Control) {
    return control.validators?.some(validator => validator.type === "required");
  }

  addToFormArray(formGroup: FormGroup, formArrayName: string, config: DynamicForm): void {
    const formArray = this.getOrCreateFormArray(formGroup, formArrayName);
    const sectionGroup = this.fb.group({});
    this.addControlsToForm(sectionGroup, config);
    formArray.push(sectionGroup);
  }

  addControlsToFormArray(formArray: FormArray, config: DynamicForm): void {
    const sectionGroup = this.fb.group({});
    this.addControlsToForm(sectionGroup, config);
    formArray.push(sectionGroup);
  }

  createEmptyFormGroup() {
    return this.fb.group({});
  }

  addControlToFormGroup(formGroup: FormGroup, formControlName: string, defaultValue?: any, disabled?: boolean, validators?: ValidatorFn[] | undefined) {
    formGroup.addControl(
      formControlName,
      this.fb.control({
        value: defaultValue ?? "",
        disabled: disabled ?? false
      }, validators ?? [])
    );
  }

  getOrCreateFormArray(formGroup: FormGroup, name: string): FormArray {
    if (!formGroup.get(name)) {
      formGroup.addControl(name, this.fb.array([]));
    }
    return formGroup.get(name) as FormArray;
  }

  getValidators(validators: Validator[], formGroup: FormGroup): ValidatorFn[] {
    return validators.filter(validator => this.evaluateConditionSet(validator?.conditions, formGroup, true))
      .map(validator =>
        this.validatorMap[validator.type]?.(validator?.params)
      )
      .filter(Boolean) as ValidatorFn[];
  }

  setValueToFormControl(formGroup: FormGroup, formControlName: string, value: any) {
    if (formGroup && formGroup.controls[formControlName]) {
      formGroup.patchValue({ [formControlName]: value });
    }
  }

  evaluateConditionSet(conditionSet: ConditionSet | undefined, formGroup: FormGroup, defaultValue: boolean): boolean {
    if (!conditionSet) return defaultValue;

    const results = conditionSet.rules.map(rule =>
      this.evaluateSingleRule(rule, formGroup)
    );

    return conditionSet.logic === "OR"
      ? results.some(Boolean)
      : results.every(Boolean);
  }

  evaluatePermissionConditionSet(conditionSet: PermissionConditionSet | undefined, defaultValue: boolean): boolean {
    if (!conditionSet?.permissionIds?.length) return defaultValue;
    const hasPermission = this.permissionService.evaluatePermissionsForLoggedInUser(conditionSet);
    return defaultValue ? hasPermission : !hasPermission;
  }


  private evaluateSingleRule(rule: Rule, formGroup: FormGroup): boolean {
    const fieldValue = formGroup.get(rule.field)?.value;
    switch (rule.operator) {
      case "EQUALS":
        return fieldValue === rule.value;
      case "NOT_EQUALS":
        return fieldValue !== rule.value;
      case "GREATER_THAN":
        return fieldValue > rule.value;
      case "LESS_THAN":
        return fieldValue < rule.value;
      case "IN":
        return Array.isArray(rule.value) && rule.value.includes(fieldValue);
      case "NOT_IN":
        return Array.isArray(rule.value) && !rule.value.includes(fieldValue);
      default:
        return false;
    }
  }

  getControl(dynamicForm: DynamicForm, formControlName: string): Control | undefined {
    return dynamicForm.formRows.flatMap(row => row.formGroups).find((formGroup: any) => formGroup?.control?.label === formControlName)?.control;
  }

}

